/**
 * @changelog
 *
 * 2026-03-02 (0.0.1) (jhoward)
 *   - Import main.css for global styles in app entry point.
 *
 * 2026-03-30 (0.0.2) (jhoward)
 *   - Import useAppStore and add commented-out import for pepHelperFunctions.
 *
 * 2026-09-18 (0.1.0) (jhoward)
 *   - Register the 'account-id' input pattern, so the three views that collect an account number describe it the same
 *     way and say so before it has been typed wrongly rather than after.
 *
 * 2026-10-06 (0.2.0) (jhoward)
 *   - Give 'account-id' a placeholder that shows the prefixed form, and a case-insensitive regex. '8 or 9 digits' hid
 *     the 2A/2B/2C prefix the field accepts, and the regex rejected 2a010000003.
 *
 * 2026-10-09 (0.3.0) (jhoward)
 *   - Import the framework's Material Design 3 foundations (peppermill.css). This app builds its own shell and never
 *     mounts <peppermill-app-container>, which is what pulls the stylesheet in, so as of framework 3.20 none of the M3
 *     tokens (--md-sys-color-*, type scale, shape, motion) existed and the framework components rendered unthemed.
 *
 * 2026-11-02 (0.4.0) (jhoward)
 *   - The 'account-id' placeholder shows the number as printed on the card, with no prefix: the property is read from
 *     the number, and the prefix is optional.
 */

import registerInputPatterns from '@framework/helpers/registerInputPatterns';
import { createPinia } from 'pinia';
import { createApp } from 'vue';
import '@framework/styles/peppermill.css';
import App from './App.vue';
import router from './router';
//Side-effect import, kept last so the stylesheet is the final thing this entry point pulls in.
import '@/main.css';
// import '@/helpers/pepHelperFunctions';

/*
 * An optional 2A/2B/2C prefix in front of eight or nine digits is not a fixed-slot shape, so this is a hint: it
 * supplies the placeholder and the expression, and leaves the value alone. The wording is the one lang\en\validation.php
 * uses for custom.account_id.regex, so the field and the server's rejection describe the same thing.
 *
 * No inputMode on purpose. 'numeric' would raise a keypad that cannot type the prefix a minority of cards carry, and
 * the prefix is the part a guest is least sure about.
 */
registerInputPatterns({
  'account-id': {
    mask: '',
    placeholder: 'e.g. 010000003',
    regex: '^(2[ABC])?[0-9]{8,9}$',
    //Case-insensitive, as the server's rules are: 2a010000003 is the same account as 2A010000003.
    regexMod: 'i',
  },
});

const app = createApp(App);
app.use(createPinia());
app.use(router);
app.mount('#app');
